import React, { useState } from 'react';
import { config } from '../config/config';

const WelcomeScreen = ({ nextStep, appState, updateAnalysisMode }) => {
  const [showModeSelection, setShowModeSelection] = useState(false);

  const handleGetStarted = () => {
    setShowModeSelection(true);
  };

  const handleModeSelect = (mode) => {
    updateAnalysisMode(mode);
    nextStep();
  };

  const pageData = {
    title: "Understand Your Skin Better",
    subtitle: "Professional-grade AI skin analysis in seconds. Discover what your skin is telling you - completely free, completely private.",
    showVideo: true
  };

  // Mode Selection Overlay
  const ModeSelectionOverlay = () => (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setShowModeSelection(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 bg-white rounded-3xl p-6 shadow-2xl animate-slide-up">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Analysis</h2>
        <p className="text-gray-600 mb-6">Select how you'd like to proceed</p>

        {/* Quick Analysis Option */}
        <button
          onClick={() => handleModeSelect('quick')}
          className="w-full mb-3 p-5 rounded-2xl border-2 border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all duration-200 text-left group"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-900 group-hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-lg">Quick Analysis</h3>
              <p className="text-gray-500 text-sm mt-1">Get instant results with just your photo and basic info</p>
            </div>
          </div>
        </button>

        {/* Detailed Analysis Option */}
        <button
          onClick={() => handleModeSelect('detailed')}
          className="w-full p-5 rounded-2xl border-2 border-gray-900 bg-gray-900 text-white transition-all duration-200 text-left"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-lg">Detailed Analysis</h3>
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">Recommended</span>
              </div>
              <p className="text-white/80 text-sm mt-1">Answer a few questions for personalized, more accurate results</p>
            </div>
          </div>
        </button>

        {/* Close button */}
        <button
          onClick={() => setShowModeSelection(false)}
          className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-full overflow-hidden relative">
      {/* Mode Selection Overlay */}
      {showModeSelection && <ModeSelectionOverlay />}

      {/* Background - Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover md:object-[80%_center]"
        >
          <source src="/video/skin-analysis-bg.mp4" type="video/mp4" />
        </video>
        {/* Gradient overlay for video */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70 md:bg-gradient-to-r md:from-black/60 md:via-black/30 md:to-transparent"></div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex md:h-full relative z-10">
        {/* Left Content Area - Desktop */}
        <div className="w-1/2 flex flex-col justify-center px-16 py-12">
          {/* Content */}
          <div className="space-y-8">

            {/* Title */}
            <h1 className="text-white text-5xl font-bold leading-tight tracking-tight">
              {pageData.title}
            </h1>

            {/* Subtitle */}
            <p className="text-white/90 text-xl leading-relaxed max-w-md">
              {pageData.subtitle}
            </p>

            {/* Apple-like Gradient Button - Desktop */}
            <div className="pt-8">
              <button
                onClick={handleGetStarted}
                className="group relative overflow-hidden bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-lg border border-white/20 hover:border-white/40 rounded-2xl px-12 py-5 text-white font-semibold text-lg tracking-wide transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] shadow-2xl hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transform hover:rotate-1"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Start Analysis
                  <svg className="w-6 h-6 ml-4 transition-transform duration-300 group-hover:translate-x-2 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                {/* Enhanced shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-[shimmer_2s_ease-out] opacity-0 group-hover:opacity-100"></div>
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>

          </div>
        </div>

        {/* Right Side - Video/Background Space */}
        <div className="w-1/2"></div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden relative z-10 h-full flex flex-col">
        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center items-center px-8 pt-20">

          {/* Title */}
          <h1 className="text-white text-3xl font-bold text-center leading-tight mb-6">
            {pageData.title}
          </h1>

          {/* Subtitle */}
          <p className="text-white/90 text-lg text-center leading-relaxed max-w-sm mb-12">
            {pageData.subtitle}
          </p>
        </div>

        {/* Bottom Section */}
        <div className="px-8 pb-8 space-y-6">
          {/* Apple-like Gradient Button - Mobile */}
          <button
            onClick={handleGetStarted}
            className="w-full group relative overflow-hidden rounded-3xl transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] transform active:rotate-1"
          >
            {/* Gradient background with glass effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/25 via-white/15 to-white/20 backdrop-blur-xl rounded-3xl"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-3xl"></div>

            {/* Button content */}
            <div className="relative z-10 px-8 py-5 border border-white/30 rounded-3xl group-hover:border-white/50 transition-all duration-300">
              <span className="flex items-center justify-center text-white font-semibold text-lg tracking-wide">
                Start Analysis
                <svg className="w-5 h-5 ml-3 transition-transform duration-300 group-hover:translate-x-1 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </div>

            {/* Enhanced shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-[shimmer_2s_ease-out] opacity-0 group-hover:opacity-100 rounded-3xl"></div>
            {/* Pulse effect */}
            <div className="absolute inset-0 rounded-3xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
          </button>


          {/* Home Indicator */}
          <div className="flex justify-center">
            <div className="w-32 h-1 bg-white/30 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
